/**
 * 성장도표 — 명세서 §5.11
 *
 * 질병관리청 2017 성장도표의 **분위수 앵커**(z-점수 ↔ 계측값)로 백분위 참조선을 그리고,
 * 아이의 계측값이 또래 분포의 어느 위치인지 계산한다. 데이터는 `growth-reference.ts`
 * (자동 생성)에 있고, 여기에는 계산만 둔다.
 *
 * ── 배포 수치를 그대로 쓴다
 * LMS 같은 분포 파라미터를 추정하지 않는다. 배포 표의 백분위수(3~97th)와
 * 표준점수(±3SD)를 앵커로 싣고, 그 **사이 값만 단조 선형 보간**한다.
 * 3rd·50th·97th 참조선은 배포 표의 해당 열과 정확히 일치한다.
 *
 * ── 판정하지 않는다 (§6.1-3)
 * 참조선은 "또래 아이들이 대체로 어디쯤 있는가"라는 **배경**이지 등급이 아니다.
 * 이 모듈은 백분위 **수치**만 돌려주고, "정상/주의" 같은 판정은 만들지 않는다.
 * 화면 문구는 `copy.ts`를 통해서만 쓴다.
 */
import { GROWTH_QUANTILES, GROWTH_Z_ANCHORS } from './growth-reference.js';
export { GROWTH_QUANTILES, GROWTH_Z_ANCHORS };
/**
 * 참조 데이터 판(版).
 *
 * 현재 적재본은 **2017 소아청소년 성장도표**(질병관리청·대한소아과학회)다.
 * 원본은 `docs/kdca-refs/`의 배포 표 PDF이고, 개정판이 나오면 PDF를 갈아 끼운 뒤
 * `pnpm --filter @saesak/shared gen:growth`를 다시 돌리고 이 상수를 함께 고친다.
 *
 * 성장도표는 약 10년 주기로 개정되어 **다음 개정은 2027년 예정**이다.
 * 화면과 생활기록부 PDF가 이 상수를 그대로 표시해, 어떤 기준으로 그린 선인지 드러낸다.
 */
export const GROWTH_REFERENCE_EDITION = {
    label: '2017 소아청소년 성장도표',
    publisher: '질병관리청',
    publishedYear: 2017,
    /** 10년 주기 개정 — 개정판이 나오면 원본 PDF와 이 상수를 함께 바꾼다 */
    nextRevisionYear: 2027,
    source: 'docs/kdca-refs/ (2017 성장도표 백분위수·표준점수 표 PDF)',
};
/** 참조선으로 그릴 백분위 (§5.11) — 배포 표에 그대로 있는 열이다 */
export const REFERENCE_PERCENTILES = [3, 50, 97];
/** 원아 성별 표기가 제각각이라(`남`/`M`/`male`) 한 곳에서 정규화한다. */
export function normalizeSex(value) {
    if (!value)
        return null;
    const v = value.trim().toLowerCase();
    if (['남', '남자', 'm', 'male', 'boy'].includes(v))
        return 'male';
    if (['여', '여자', 'f', 'female', 'girl'].includes(v))
        return 'female';
    return null;
}
/** 참조 데이터가 덮는 개월수 범위 (2017 표는 정수 개월 행) */
export function coveredMonths(metric) {
    return { from: 0, to: GROWTH_QUANTILES[metric].male.length - 1 };
}
/**
 * 해당 나이의 앵커 값 목록 (z 오름차순, `GROWTH_Z_ANCHORS`와 같은 길이).
 *
 * 표는 정수 개월 행이므로 소수 개월은 앞뒤 행을 선형 보간한다.
 * 범위 밖이면 null — 임의로 추정하지 않는다.
 */
export function anchorsFor(metric, sex, months) {
    const rows = GROWTH_QUANTILES[metric]?.[sex];
    if (!rows || rows.length === 0)
        return null;
    if (months < 0 || months > rows.length - 1)
        return null;
    const lo = Math.floor(months);
    const hi = Math.min(lo + 1, rows.length - 1);
    const t = months - lo;
    if (t === 0 || lo === hi)
        return [...rows[lo]];
    return rows[lo].map((v, i) => v + (rows[hi][i] - v) * t);
}
/**
 * z-점수 — 계측값을 앵커 사이에서 단조 보간해 얻는다.
 * 최상·최하 앵커(±3SD) 밖은 끝 구간의 기울기로 연장하되 ±4로 자른다.
 */
export function zScoreOf(metric, sex, months, value) {
    const anchors = anchorsFor(metric, sex, months);
    if (!anchors || value <= 0)
        return null;
    // 표가 0.1 단위로 반올림돼 이웃 앵커 값이 같아질 수 있다(분포가 촘촘한 구간).
    // 같은 값의 앵커들은 z 평균 하나로 압축한다 — 그 값의 참 z 는 그 범위 어딘가이고,
    // 중앙이 편향 없는 추정이다. 압축 후에는 값이 강한 단조라 보간이 유일하게 정해진다.
    const xs = [];
    const zs = [];
    let i = 0;
    while (i < anchors.length) {
        let j = i;
        let zSum = 0;
        while (j < anchors.length && anchors[j] - anchors[i] < 1e-9) {
            zSum += GROWTH_Z_ANCHORS[j];
            j += 1;
        }
        xs.push(anchors[i]);
        zs.push(zSum / (j - i));
        i = j;
    }
    const z = interpolate(xs, zs, value);
    return Math.max(-4, Math.min(4, z));
}
/** 백분위(0–100). 극단값은 0.1–99.9로 잘라 "0%" 같은 표시가 나오지 않게 한다. */
export function percentileOf(metric, sex, months, value) {
    const z = zScoreOf(metric, sex, months, value);
    if (z === null)
        return null;
    return Math.min(99.9, Math.max(0.1, normalCdf(z) * 100));
}
/** 해당 백분위의 계측값 — 참조선 좌표를 만들 때 쓴다. */
export function valueAtPercentile(metric, sex, months, percentile) {
    const anchors = anchorsFor(metric, sex, months);
    if (!anchors || percentile <= 0 || percentile >= 100)
        return null;
    const z = inverseNormalCdf(percentile / 100);
    return interpolate([...GROWTH_Z_ANCHORS], anchors, z);
}
/**
 * 참조선 한 줄의 좌표들.
 * 데이터가 없는 개월수는 건너뛴다(머리둘레는 72개월까지만 있다).
 */
export function referenceCurve(metric, sex, percentile, fromMonths, toMonths) {
    const { to } = coveredMonths(metric);
    const start = Math.max(0, Math.floor(fromMonths));
    const end = Math.min(to, Math.ceil(toMonths));
    const points = [];
    for (let months = start; months <= end; months += 1) {
        const value = valueAtPercentile(metric, sex, months, percentile);
        if (value !== null)
            points.push({ months, value });
    }
    return points;
}
/**
 * 단조 증가 표 (xs → ys)에서 x에 해당하는 y를 선형 보간.
 * 범위 밖은 끝 구간 기울기로 연장한다(호출부가 필요하면 자른다).
 */
function interpolate(xs, ys, x) {
    const n = xs.length;
    let i = 1;
    while (i < n - 1 && xs[i] < x)
        i += 1;
    // 동일 x 앵커(반올림으로 값이 같은 이웃)는 기울기 0 division 을 피해 건너뛴다.
    let lo = i - 1;
    while (lo > 0 && xs[i] - xs[lo] < 1e-9)
        lo -= 1;
    const span = xs[i] - xs[lo];
    if (span < 1e-9)
        return ys[i];
    const t = (x - xs[lo]) / span;
    return ys[lo] + (ys[i] - ys[lo]) * t;
}
/** 표준정규 누적분포 — Abramowitz & Stegun 7.1.26 오차함수 근사 */
function normalCdf(z) {
    return 0.5 * (1 + erf(z / Math.SQRT2));
}
function erf(x) {
    const sign = x < 0 ? -1 : 1;
    const t = 1 / (1 + 0.3275911 * Math.abs(x));
    const y = 1 -
        ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
            t *
            Math.exp(-x * x);
    return sign * y;
}
/** 표준정규 분위수 — Acklam 근사 */
function inverseNormalCdf(p) {
    const a = [
        -3.969683028665376e1, 2.20946098424521e2, -2.759285104469687e2, 1.38357751867269e2,
        -3.066479806614716e1, 2.506628277459239,
    ];
    const b = [
        -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1,
        -1.328068155288572e1,
    ];
    const c = [
        -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734,
        4.374664141464968, 2.938163982698783,
    ];
    const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
    const low = 0.02425;
    if (p <= 0)
        return -Infinity;
    if (p >= 1)
        return Infinity;
    if (p < low) {
        const q = Math.sqrt(-2 * Math.log(p));
        return ((((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
            ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1));
    }
    if (p <= 1 - low) {
        const q = p - 0.5;
        const r = q * q;
        return (((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
            (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1));
    }
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return (-(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
        ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1));
}
//# sourceMappingURL=growth.js.map